import React, { FC, useState, useContext, useEffect } from "react";
import { Box } from "@mui/material";
import { SharedProjectContext } from "components/project/Project";
import useProjectStore from "../../../../common/store/ProjectStore";
import ScaleDialog, { ScaleStep } from "components/project/ScaleDialog";

interface IProps {
  plotImageTransform: number;
}

const ScaleOverlay: FC<IProps> = ( { plotImageTransform }) => {
  const { scaleSet, setScaleSet, setIsBusyBackgroundActive } = useContext(SharedProjectContext);
  const [scaleCoords1, setScaleCoords1] = useState({ x1: -1, y1: -1 });
  const [scaleCoords2, setScaleCoords2] = useState({ x2: -1, y2: -1 });
  const [drawLine, setDrawLine] = useState(false);
  const [draw, setDraw] = useState(false);
  const [scaleModalOpen, setScaleModalOpen] = useState<boolean>(false);

  const { setScalingFactor, zoomPercent, setDistanceID } = useProjectStore();

  useEffect(() => {
    setIsBusyBackgroundActive(scaleModalOpen);
  }, [scaleModalOpen, setIsBusyBackgroundActive])

  const handleMouseDown = (event: React.MouseEvent) => {
    var boxElement = document.getElementById("drop-box");
    if (boxElement) {
      var box = boxElement.getBoundingClientRect();
      setScaleCoords1({
        x1: (event.clientX - box.left) / (zoomPercent/100),
        y1: (event.clientY - box.top) / (zoomPercent/100),
      });
    }
    setDraw(true);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    var boxElement = document.getElementById("drop-box");
    if (boxElement) {
      var box = boxElement.getBoundingClientRect();
      setScaleCoords2({
        x2: (event.clientX - box.left) / (zoomPercent/100),
        y2: (event.clientY - box.top) / (zoomPercent/100),
      });
      if (!drawLine && scaleCoords1.x1 !== event.clientX - box.left && scaleCoords1.y1 !== event.clientY - box.top) {
        setDrawLine(true);
      }
    }
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    if (drawLine) {
      setDraw(false);
      var boxElement = document.getElementById("drop-box");
      if (boxElement) {
        var box = boxElement.getBoundingClientRect();
        setScaleCoords2({
          x2: (event.clientX - box.left) / (zoomPercent/100),
          y2: (event.clientY - box.top) / (zoomPercent/100),
        });
      }
      setScaleModalOpen(true);
    } else {
      //handle clicks
      setDraw(false);
    }
  };

  const handleScaleDialogConfirm = () => {
    setScaleModalOpen(false)
    // Even tho we have a useEffect that should handle setting busy flag, this component is pulled from the visual tree immediately when scale is set
    setIsBusyBackgroundActive(false);
  }

  // Called on ScaleModal Confirm
  const calculateScale = (scaleDistance: number, distanceUnitID: number) => {
    try {
      if (scaleDistance && scaleDistance > 0) {
        let pixels = Math.pow(scaleCoords1.x1 - scaleCoords2.x2, 2) + Math.pow(scaleCoords1.y1 - scaleCoords2.y2, 2);
        pixels = Math.sqrt(pixels);
        setScalingFactor(pixels / plotImageTransform / scaleDistance);
        setDistanceID(distanceUnitID);
      } else {
        throw new Error("Distance input null or less than or equal to 0");
      }
    } catch (err) {
      console.log(err);
    }

    setScaleSet(false);
    setDrawLine(false);
    setDraw(false);
    setScaleCoords1({ x1: -1, y1: -1 });
    setScaleCoords2({ x2: -1, y2: -1 });
  };

  const scaleModalCancel = () => {
    setScaleCoords1({ x1: -1, y1: -1 });
    setScaleCoords2({ x2: -1, y2: -1 });
    setDrawLine(false);
    setDraw(false);
    setScaleModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          top: 0,
          left: 0,
          position: "absolute",
          bgcolor: "transparent",
          zIndex: 5,
          height: "100%",
          width: "100%",
          "&:hover": {
            cursor: "crosshair",
          },
        }}
        onMouseDown={scaleSet ? (e) => handleMouseDown(e) : undefined}
        onMouseMove={scaleSet && draw ? (e) => handleMouseMove(e) : undefined}
        onMouseUp={scaleSet ? (e) => handleMouseUp(e) : undefined}
      >
        <svg height="100%" width="100%">
          {scaleSet && drawLine && scaleCoords1.x1 >= 0 && scaleCoords1.y1 >= 0 && (
            <line x1={scaleCoords1.x1} y1={scaleCoords1.y1} x2={scaleCoords2.x2} y2={scaleCoords2.y2} stroke="red" />
          )}
        </svg>
      </Box>
      <ScaleDialog
        stepNumber={ScaleStep.SetScale}
        isOpen={scaleModalOpen}
        handleClose={scaleModalCancel}
        handleConfirm={handleScaleDialogConfirm}
        setScale={calculateScale}
      />
    </>
  );
};

export default ScaleOverlay;
